import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  CubeIcon,
  ArrowRightIcon,
  UserIcon,
  CogIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import QuantumSecurityIndicator from './QuantumSecurityIndicator';

const Header = ({ isConnected, userAddress, onConnect, onDisconnect, bridgeStatus }) => {
  const location = useLocation();
  const navLinks = [
    { to: '/', label: 'Dashboard' },
    { to: '/assets', label: 'Assets' },
    { to: '/bridge', label: 'Bridge' },
    { to: '/avatar', label: 'Avatar' }
  ];
  return (
    <header
      className="relative z-10 border-b"
      style={{ background: 'var(--galaxia-nav)', borderColor: 'var(--galaxia-border)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Navigation */}
          <div className="flex items-center">
            <div className="flex-shrink-0 flex items-center gap-2">
              <img
                src={`${process.env.PUBLIC_URL || ''}/galaxia-icon.svg`}
                alt="Galaxia"
                className="galaxia-logo-glow h-9 w-9 sm:hidden"
                width={36}
                height={36}
              />
              <img
                src={`${process.env.PUBLIC_URL || ''}/brand-lockup.svg`}
                alt="Galaxia Metaverse Bridge"
                className="galaxia-logo-glow h-10 hidden sm:block"
              />
              <h1 className="text-xl font-bold sm:hidden" style={{ color: 'var(--galaxia-text)' }}>
                <span className="galaxia-gradient-text">Metaverse Bridge</span>
              </h1>
            </div>
            <nav className="hidden md:ml-8 md:flex space-x-1">
              {navLinks.map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  className="px-3 py-2 text-sm font-medium rounded-lg transition-colors"
                  style={{
                    color: location.pathname === to ? 'var(--galaxia-accent)' : 'var(--galaxia-muted)'
                  }}
                >
                  {label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Bridge Status and Wallet */}
          <div className="flex items-center space-x-4">
            <div className="hidden lg:block">
              <QuantumSecurityIndicator />
            </div>
            {bridgeStatus && (
              <div className="hidden sm:flex items-center">
                <div
                  className="w-2 h-2 rounded-full mr-2"
                  style={{
                    background: bridgeStatus.bridge.isActive ? 'var(--galaxia-success)' : 'var(--galaxia-error)'
                  }}
                />
                <span className="text-sm" style={{ color: 'var(--galaxia-muted)' }}>
                  {bridgeStatus.bridge.isActive ? 'Bridge Active' : 'Bridge Inactive'}
                </span>
              </div>
            )}

            {isConnected ? (
              <div className="flex items-center space-x-3">
                <span className="text-sm font-mono" style={{ color: 'var(--galaxia-muted)' }}>
                  {userAddress.slice(0, 6)}...{userAddress.slice(-4)}
                </span>
                <button
                  onClick={onDisconnect}
                  className="galaxia-btn-secondary px-3 py-2 text-sm"
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <button onClick={onConnect} className="galaxia-btn-primary px-4 py-2 text-sm">
                Connect Wallet
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

const Sidebar = ({ isOpen, onClose, isConnected }) => {
  const location = useLocation();

  const navigation = [
    { name: 'Dashboard', href: '/', icon: HomeIcon },
    { name: 'Assets', href: '/assets', icon: CubeIcon },
    { name: 'Bridge', href: '/bridge', icon: ArrowRightIcon },
    { name: 'Avatar', href: '/avatar', icon: UserIcon },
    { name: 'Settings', href: '/settings', icon: CogIcon }
  ];

  if (!isOpen) return null;

  const isActive = (href) => location.pathname === href;

  return (
    <>
      <div
        className="fixed inset-0 z-40 lg:hidden"
        style={{ background: 'rgba(0,0,0,0.6)' }}
        onClick={onClose}
        aria-hidden
      />
      <div
        className="fixed inset-y-0 left-0 z-50 w-64 shadow-lg transform transition-transform lg:hidden"
        style={{ background: 'var(--galaxia-nav)', borderRight: '1px solid var(--galaxia-border)' }}
      >
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--galaxia-border)' }}>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--galaxia-text)' }}>Menu</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:opacity-80" style={{ color: 'var(--galaxia-muted)' }}>
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <nav className="mt-4">
          {navigation.map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={onClose}
                className={`flex items-center px-4 py-3 text-sm font-medium transition-colors ${
                  active ? 'border-r-2' : ''
                }`}
                style={{
                  background: active ? 'rgba(206, 147, 216, 0.12)' : 'transparent',
                  borderColor: active ? 'var(--galaxia-accent)' : 'transparent',
                  color: active ? 'var(--galaxia-accent)' : 'var(--galaxia-muted)'
                }}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div
          className="absolute bottom-0 left-0 right-0 p-4 border-t"
          style={{ borderColor: 'var(--galaxia-border)' }}
        >
          {isConnected ? (
            <div className="flex items-center text-sm" style={{ color: 'var(--galaxia-success)' }}>
              <div className="w-2 h-2 rounded-full mr-2" style={{ background: 'var(--galaxia-success)' }} />
              Wallet Connected
            </div>
          ) : (
            <div className="flex items-center text-sm" style={{ color: 'var(--galaxia-muted)' }}>
              <div className="w-2 h-2 rounded-full mr-2" style={{ background: 'var(--galaxia-border)' }} />
              Wallet Disconnected
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export { Header, Sidebar };
